import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import logoAsset from "@/assets/restore/thairapy/Thairapy-logo.png.asset.json";
import heroAsset from "@/assets/restore/thairapy/banner-Image-3.jpg.asset.json";
import checkCircleAsset from "@/assets/restore/thairapy/Group-152-2-2.png.asset.json";
import head1Asset from "@/assets/restore/thairapy/women-head-icon1.png.asset.json";
import head2Asset from "@/assets/restore/thairapy/women-head-icon2.png.asset.json";
import head3Asset from "@/assets/restore/thairapy/women-head-icon3.png.asset.json";
import confidenceAsset from "@/assets/restore/thairapy/AdobeStock_282128453-1.jpg.asset.json";
import goldCheckAsset from "@/assets/restore/thairapy/icon.png.asset.json";
import femaleLoss1Asset from "@/assets/restore/thairapy/Female-hair-loss-1.jpg.asset.json";
import femaleLoss2Asset from "@/assets/restore/thairapy/Female-hair-loss-2-1.jpg.asset.json";
import doctorsAsset from "@/assets/restore/thairapy/dr.james_Dr.Mary_.webp.asset.json";
import studiesAsset from "@/assets/restore/thairapy/Independent-studies-1.jpg.asset.json";
import stage1Asset from "@/assets/restore/thairapy/2022-03-16_10-25-32.webp.asset.json";
import stage2Asset from "@/assets/restore/thairapy/2022-03-16_10-25-321.webp.asset.json";
import stage3Asset from "@/assets/restore/thairapy/2022-03-16_10-25-322.webp.asset.json";


const TITLE = "Thairapy by RESTORE® | Hair Loss Solutions for Women";
const DESCRIPTION =
  "Thairapy is a clinically proven, physician-customized hair growth program for women with hair loss — from just $299 a month. Real science. Real results.";
const URL = "https://restorehair.com/thairapy/";
const QUOTE_URL = "/quick-quote-1-female/";

const hairLossTypes = [
  { img: head1Asset.url, label: "Type I Mild" },
  { img: head2Asset.url, label: "Type II Moderate" },
  { img: head3Asset.url, label: "Type III Severe" },
];

const benefits = [
  "Boost Your Confidence",
  "Up To 20% More Hair In Just 6 Months*",
  "Real Science. Real Results.",
];

const treatments = [
  {
    title: "Oral and Topical Medications",
    body: "A personalized regimen may include Minoxidil and a specially formulated anti-inflammatory shampoo.",
  },
  {
    title: "Micronutrient Supplements",
    body: "Fight hair loss and promote hair growth from the inside out.",
  },
  {
    title: "In-office Hair Stimulating Treatments",
    body: "Long used to aid in the body's recovery from injuries, these treatments use that same power to stimulate resting hair follicles and promote hair growth.",
  },
  {
    title: "Low-level Laser Light Therapy",
    body: "Stimulates cells at the base of the follicle to prevent future hair loss and promote hair growth.",
  },
];

const timeline = [
  {
    img: stage1Asset.url,
    title: "Repair",
    range: "1 – 3 Months",
    points: ["Reduced hair loss", "Increased hair"],
  },
  {
    img: stage2Asset.url,
    title: "Healthier Hair",
    range: "2 – 6 Months",
    points: ["Reduced hair loss", "Increased hair"],
  },
  {
    img: stage3Asset.url,
    title: "Growth",
    range: "6 – 9 Months",
    points: ["Visible new hair growth on top and crown of head", "Thicker and longer hair"],
  },
];

const tips = [
  { label: "Make it a habit", body: "Use your Thairapy program every day. It will become a habit in no time." },
  { label: "Be consistent", body: "The more consistent you are with treatments, the better those before and after photos will look!" },
  { label: "Be patient", body: "Hair restoration therapies start at the very root of the hair and begin working deep beneath the surface. It can take 16-24 weeks to see visible results." },
];

export const Route = createFileRoute("/thairapy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ThairapyPage,
});

function ThairapyPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 lg:grid-cols-2 lg:gap-0 lg:px-8">
            <div className="py-10 md:py-16">
              <img
                src={logoAsset.url}
                alt="Thairapy by RESTORE® logo"
                className="h-auto w-44 md:w-56"
              />
              <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl">
                RESTORE<sup className="align-super text-base">®</sup> Your Hair &amp;
                <span className="block">Your Confidence</span>
              </h1>
              <p className="mt-5 max-w-lg text-lg text-muted-foreground">
                Get your hair back with the clinically proven solution for women with hair loss for
                just $299 a month.
              </p>
              <a
                href={QUOTE_URL}
                className="mt-8 inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground"
              >
                Get Started Now
              </a>
            </div>
            <div className="lg:h-[560px]">
              <img
                src={heroAsset.url}
                alt="Smiling woman with healthy, full hair"
                className="mx-auto h-full w-full max-w-md object-contain object-bottom lg:max-w-none"
              />
            </div>
          </div>
        </section>

        {/* Hair loss types */}
        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <p className="text-center text-base text-foreground">
              <strong>Get a quick quote.</strong> It's easy.
            </p>
            <h2 className="mx-auto mt-4 max-w-2xl text-center text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
              Which image most closely resembles your hair loss?
            </h2>
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              {hairLossTypes.map((t) => (
                <a
                  key={t.label}
                  href={QUOTE_URL}
                  className="group relative block rounded-md border border-border bg-background px-6 pb-8 pt-10 text-center transition-colors hover:border-primary"
                >
                  <img
                    src={checkCircleAsset.url}
                    alt=""
                    aria-hidden="true"
                    className="absolute -top-6 left-6 h-12 w-12"
                  />
                  <span className="mx-auto flex h-36 items-center justify-center rounded-md bg-muted/60">
                    <img src={t.img} alt={t.label} loading="lazy" className="h-auto w-28" />
                  </span>
                  <p className="mt-6 text-xl text-foreground">{t.label}</p>
                  <span className="mt-2 inline-block text-sm font-semibold uppercase tracking-wide text-primary opacity-0 transition group-hover:opacity-100">
                    Click Here Now
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-12 md:py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
            <img
              src={confidenceAsset.url}
              alt="Woman celebrating with healthy, full hair after Thairapy by RESTORE®"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <ul className="space-y-8 px-4 lg:px-8">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-4">
                  <img src={goldCheckAsset.url} alt="" aria-hidden="true" className="h-9 w-9" />
                  <span className="text-lg font-bold text-foreground">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* What is Thairapy */}
        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-2 lg:px-8">
            <div>
              <h2 className="text-4xl tracking-tight text-foreground">
                What is <strong className="font-extrabold">Thairapy?</strong>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Thairapy is not a product or a procedure. It's a comprehensive treatment program that
                harnesses the most innovative solutions to help stop hair loss and regrow your hair.
                Your physician will customize a combination of proven-effective treatments to address
                the specific causes of your hair loss and deliver the best results. It's discreet,
                individualized— and it really works.
              </p>
            </div>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {treatments.map((t, i) => (
                <AccordionItem
                  key={t.title}
                  value={`item-${i}`}
                  className="border border-border bg-background px-5"
                >
                  <AccordionTrigger className="text-left text-lg font-bold text-foreground hover:no-underline">
                    {t.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                    {t.body}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Understanding female hair loss */}
        <section className="py-12 md:py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
            <img
              src={femaleLoss1Asset.url}
              alt="Woman washing her hair"
              loading="lazy"
              className="h-full min-h-[320px] w-full object-cover"
            />
            <div className="px-4 lg:px-8">
              <h2 className="text-4xl tracking-tight text-foreground">
                Understanding
                <span className="block font-extrabold">Female Hair Loss</span>
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
                <p>
                  No one talks about female hair loss, so you suffer alone. But you're not alone:
                  just as many women suffer from hair loss as men.
                </p>
                <p>
                  The emotional impact for women is much greater, however. Hair loss can affect your
                  emotional wellbeing and diminish your quality of life. Women's options are
                  different, too. Most women aren't candidates for a hair transplant and the most
                  popular prescription medication isn't approved for women.
                </p>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-12 grid max-w-7xl items-center gap-10 lg:grid-cols-2">
            <div className="order-2 px-4 lg:order-1 lg:px-8">
              <h2 className="text-4xl tracking-tight text-foreground">
                Not All Hair Loss
                <span className="block font-extrabold">Is The Same</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                Most female pattern hair loss is due to a genetic condition called androgenic
                alopecia. Instead of a receding hairline or bald spot, as is common in men, women
                tend to have visible thinning over the crown.
              </p>
            </div>
            <img
              src={femaleLoss2Asset.url}
              alt="Woman taking a hair growth supplement"
              loading="lazy"
              className="order-1 h-full min-h-[320px] w-full object-cover lg:order-2"
            />
          </div>
        </section>

        {/* Trust the experts */}
        <section className="bg-muted pt-12 md:pt-16">
          <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
            <h2 className="text-4xl tracking-tight text-foreground md:text-5xl">
              Trust Your Hair to the <strong className="font-extrabold">Experts</strong>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Thairapy is brought to you by RESTORE®, a leader and innovator in hair growth and
                restoration for more than 20 years. Led by Medical Director Dr. James Harris, an
                internationally recognized expert in hair restoration, RESTORE has successfully
                helped thousands of patients stop hair loss and grow thicker, fuller hair.
              </p>
              <p>
                Your specialized Thairapy team will work with you, individually and personally, to
                address the causes of your hair loss and customize your treatment plan for best
                results.
              </p>
            </div>
          </div>

          <div className="relative mx-auto mt-8 max-w-7xl">
            <img
              src={doctorsAsset.url}
              alt="Dr. James Harris and Dr. Mary Bizios"
              loading="lazy"
              className="mx-auto block h-auto w-full max-w-3xl"
            />
            <div className="grid gap-6 px-4 pb-10 text-center md:absolute md:inset-x-0 md:top-1/3 md:grid-cols-2 md:px-8 md:pb-0">
              <div className="md:text-left">
                <span className="inline-block bg-primary/30 px-3 py-1 text-sm text-foreground">
                  Award-Winning Hair Doctor
                </span>
                <p className="mt-2 text-2xl font-bold text-foreground">Dr. James Harris</p>
                <p className="text-sm italic text-muted-foreground">RESTORE Medical Director</p>
              </div>
              <div className="md:text-right">
                <p className="text-2xl font-bold text-foreground">Dr. Mary Bizios</p>
                <p className="text-sm italic text-muted-foreground">Hair Restoration Physician</p>
              </div>
            </div>
          </div>
        </section>

        {/* What the experts say */}
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <h2 className="text-center text-4xl tracking-tight text-foreground md:text-5xl">
              What the <strong className="font-extrabold">Experts Say</strong>
            </h2>
            <p className="mt-6 text-center text-xl font-medium text-primary">
              A customized, comprehensive, effective hair growth program.
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              Beginning with your first treatment, you'll start reversing hair loss and rejuvenating
              your follicles to grow healthier, stronger, and thicker hair.
            </p>
            <ul className="mt-6 space-y-3">
              {tips.map((t) => (
                <li key={t.label} className="text-base leading-relaxed text-muted-foreground">
                  <strong className="text-foreground">{t.label}</strong> – {t.body}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Independent studies */}
        <section>
          <div className="grid lg:grid-cols-2">
            <img
              src={studiesAsset.url}
              alt="Physician consulting with a patient"
              loading="lazy"
              className="h-full min-h-[320px] w-full object-cover"
            />
            <div className="flex flex-col justify-center bg-[hsl(0_0%_35%)] px-6 py-12 md:px-12">
              <p className="text-2xl font-bold leading-snug text-primary">
                Independent studies on core treatment protocols used in Thairapy showed more than 80%
                of participants saw:
              </p>
              <ul className="mt-6 space-y-2 text-lg text-white/90">
                <li>• Less hair loss within 90 days</li>
                <li>• Thicker diameter at base of hair within 90 days</li>
                <li>• A 30% increase in new hair growth within 180 days.*</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-12 md:py-16">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 md:grid-cols-3 lg:px-8">
            {timeline.map((s) => (
              <div key={s.title} className="bg-muted px-6 py-10 text-center">
                <img
                  src={s.img}
                  alt={`${s.title} stage of Thairapy hair growth results`}
                  loading="lazy"
                  className="mx-auto h-auto w-40"
                />
                <h3 className="mt-8 text-3xl font-extrabold text-foreground">{s.title}</h3>
                <p className="mt-2 text-base text-muted-foreground">{s.range}</p>
                <ul className="mt-3 space-y-1 text-base text-muted-foreground">
                  {s.points.map((p) => (
                    <li key={p}>• {p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
